// Contracts — datoric AUTH (2026-08-02). Staff-only magic link; there is no
// self-signup, an account must exist in `users` before a link is issued.
import { z } from 'zod';
import { DatoricEmailSchema, DatoricRoleSchema } from './enums';

// POST /api/datoric/auth/request {email} → 200 always.
// The response is identical for known and unknown addresses: this endpoint is
// unauthenticated, and a differing answer would turn it into a staff directory.
export const DatoricAuthRequestReqSchema = z.object({ email: DatoricEmailSchema });
export type DatoricAuthRequestReq = z.infer<typeof DatoricAuthRequestReqSchema>;

export const DatoricAuthRequestResSchema = z.object({ ok: z.literal(true) });
export type DatoricAuthRequestRes = z.infer<typeof DatoricAuthRequestResSchema>;

// POST /api/datoric/auth/login {email, password} → session JWT. The primary
// door while email delivery is unavailable (0003): passwords are set by the
// manager, no self-service reset. Response indistinguishable for wrong email
// vs wrong password.
export const DatoricPasswordSchema = z.string().min(8).max(200);

export const DatoricLoginReqSchema = z.object({
  email: DatoricEmailSchema,
  password: DatoricPasswordSchema,
});
export type DatoricLoginReq = z.infer<typeof DatoricLoginReqSchema>;

export const DatoricLoginResSchema = z.object({ token: z.string().min(1) });
export type DatoricLoginRes = z.infer<typeof DatoricLoginResSchema>;

// POST /api/datoric/auth/verify {token} → session JWT. Single-use: the worker
// burns the row with an UPDATE … WHERE used_at IS NULL guard.
export const DatoricAuthVerifyReqSchema = z.object({ token: z.string().trim().min(20).max(200) });
export type DatoricAuthVerifyReq = z.infer<typeof DatoricAuthVerifyReqSchema>;

export const DatoricAuthVerifyResSchema = z.object({ token: z.string().min(1) });
export type DatoricAuthVerifyRes = z.infer<typeof DatoricAuthVerifyResSchema>;

// GET /api/datoric/auth/me → the caller. Two shapes, discriminated by role:
// staff (viewer/admin/manager) and contributor (their own cabinet, 0004).
// `showBalance` is per-person (kids 0020 heritage); `rateCents` on staff is
// the cascade ceiling, on a contributor — their own pay rate.
export const DatoricStaffMeSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: DatoricRoleSchema,
  showBalance: z.boolean(),
  rateCents: z.number().int().nullable(),
});
export type DatoricStaffMe = z.infer<typeof DatoricStaffMeSchema>;

export const DatoricContributorMeSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.literal('contributor'),
  rateCents: z.number().int().nullable(),
});
export type DatoricContributorMe = z.infer<typeof DatoricContributorMeSchema>;

export const DatoricMeResSchema = z.union([DatoricStaffMeSchema, DatoricContributorMeSchema]);
export type DatoricMeRes = z.infer<typeof DatoricMeResSchema>;
